import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, getFirestore } from 'firebase/firestore';
import {
  ArrowLeftIcon,
  ArrowUpTrayIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { auth } from '../firebase';
import {
  CONTROL_SLIDE_IMAGE_COUNT,
  normalizeControlIdentifier,
  validateControlSlideInput,
} from './controlSlides';
import { createControlSlide } from './controlSlideApi';

let nextFileId = 1;

function fileFingerprint(file) {
  return `${file.name}|${file.size}|${file.lastModified}`;
}

export default function CreateControlSlide() {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [organizationId, setOrganizationId] = useState('');
  const [controlIdentifier, setControlIdentifier] = useState('');
  const [description, setDescription] = useState('');
  const [items, setItems] = useState([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(true);
  const [organizationError, setOrganizationError] = useState('');
  const [fileError, setFileError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const itemsRef = useRef(items);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => () => {
    itemsRef.current.forEach((item) => URL.revokeObjectURL(item.previewUrl));
  }, []);

  useEffect(() => {
    let active = true;
    async function loadOrganizations() {
      setOrganizationsLoading(true);
      setOrganizationError('');
      try {
        const snapshot = await getDocs(collection(getFirestore(), 'organizations'));
        const loadedOrganizations = snapshot.docs
          .map((document) => ({ id: document.id, ...document.data() }))
          .sort((left, right) => (
            String(left.name || left.id).localeCompare(String(right.name || right.id))
          ));
        if (active) {
          setOrganizations(loadedOrganizations);
        }
      } catch (error) {
        console.error('Failed to load organizations:', error);
        if (active) {
          setOrganizationError('Organizations could not be loaded. Refresh the page to try again.');
        }
      } finally {
        if (active) {
          setOrganizationsLoading(false);
        }
      }
    }
    loadOrganizations();
    return () => {
      active = false;
    };
  }, []);

  const addFiles = useCallback((fileList) => {
    const incoming = Array.from(fileList || []);
    setFileError('');
    setValidationErrors([]);
    setSubmitError('');
    if (incoming.length === 0) {
      return;
    }
    if (incoming.some((file) => !file.type?.startsWith('image/'))) {
      setFileError('Only image files can be added.');
      return;
    }

    setItems((current) => {
      const fingerprints = new Set(current.map((item) => fileFingerprint(item.file)));
      const uniqueFiles = incoming.filter((file) => {
        const fingerprint = fileFingerprint(file);
        if (fingerprints.has(fingerprint)) {
          return false;
        }
        fingerprints.add(fingerprint);
        return true;
      });
      if (current.length + uniqueFiles.length > CONTROL_SLIDE_IMAGE_COUNT) {
        setFileError(`Select exactly ${CONTROL_SLIDE_IMAGE_COUNT} images.`);
        return current;
      }
      return [
        ...current,
        ...uniqueFiles.map((file) => ({
          id: nextFileId++,
          file,
          previewUrl: URL.createObjectURL(file),
        })),
      ];
    });
  }, []);

  const removeItem = useCallback((id) => {
    setFileError('');
    setValidationErrors([]);
    setItems((current) => {
      const removed = current.find((item) => item.id === id);
      if (removed) {
        URL.revokeObjectURL(removed.previewUrl);
      }
      return current.filter((item) => item.id !== id);
    });
  }, []);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    setDragActive(false);
    addFiles(event.dataTransfer.files);
  }, [addFiles]);

  const handleSubmit = async (event) => {
    event?.preventDefault();
    if (submitting) {
      return;
    }
    const files = items.map((item) => item.file);
    const errors = validateControlSlideInput({
      organizationId,
      controlIdentifier,
      files,
    });
    setValidationErrors(errors);
    setSubmitError('');
    if (errors.length > 0) {
      return;
    }

    setSubmitting(true);
    setProgress(0);
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error('Your admin session has expired. Sign in and try again.');
      }
      const identifier = normalizeControlIdentifier(controlIdentifier);
      await createControlSlide({
        token,
        organizationId,
        controlIdentifier: identifier,
        description,
        files,
        onProgress: setProgress,
      });
      const organization = organizations.find((candidate) => candidate.id === organizationId);
      navigate('/control-slides', {
        state: {
          successMessage: `Control ${identifier} was created for ${organization?.name || organizationId}.`,
        },
      });
    } catch (error) {
      console.error('Failed to create control slide:', error);
      setSubmitError(error.message || 'Control slide could not be created.');
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
      <button
        type="button"
        onClick={() => navigate('/control-slides')}
        disabled={submitting}
        className="inline-flex items-center gap-x-2 text-sm font-semibold text-gray-600 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
        Control slides
      </button>

      <header className="mt-5 border-b border-gray-200 pb-6">
        <p className="text-sm font-semibold uppercase tracking-wide text-indigo-600">New release unit</p>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight text-gray-900">Create a control slide</h1>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-600">
          Add one complete set of ten source images. The slide is enabled and included in manifests as a unit.
        </p>
      </header>

      <form className="mt-8" onSubmit={handleSubmit}>
        <div className="grid gap-10 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
          <section aria-labelledby="slide-details-heading">
            <h2 id="slide-details-heading" className="text-base font-semibold text-gray-900">Slide details</h2>
            <p className="mt-1 text-sm text-gray-600">The identifier is unique within an organization.</p>

            <div className="mt-6 space-y-6">
              <div>
                <label htmlFor="control-slide-organization" className="block text-sm font-medium text-gray-900">
                  Organization
                </label>
                <select
                  id="control-slide-organization"
                  value={organizationId}
                  onChange={(event) => setOrganizationId(event.target.value)}
                  disabled={organizationsLoading || submitting || Boolean(organizationError)}
                  className="mt-2 block w-full rounded-md border-gray-300 bg-white py-2 pl-3 pr-10 text-sm shadow-sm focus:border-indigo-600 focus:ring-indigo-600 disabled:bg-gray-100"
                >
                  <option value="">{organizationsLoading ? 'Loading organizations…' : 'Select an organization'}</option>
                  {organizations.map((organization) => (
                    <option key={organization.id} value={organization.id}>
                      {organization.name || organization.id}
                    </option>
                  ))}
                </select>
                {organizationError && (
                  <p role="alert" className="mt-2 text-sm text-red-700">{organizationError}</p>
                )}
              </div>

              <div>
                <label htmlFor="control-slide-identifier" className="block text-sm font-medium text-gray-900">
                  Control identifier
                </label>
                <input
                  id="control-slide-identifier"
                  type="number"
                  min="1"
                  step="1"
                  inputMode="numeric"
                  value={controlIdentifier}
                  onChange={(event) => setControlIdentifier(event.target.value)}
                  disabled={submitting}
                  placeholder="e.g. 12"
                  className="mt-2 block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-600 focus:ring-indigo-600 disabled:bg-gray-100"
                />
              </div>

              <div>
                <label htmlFor="control-slide-description" className="block text-sm font-medium text-gray-900">
                  Description <span className="font-normal text-gray-500">(optional)</span>
                </label>
                <textarea
                  id="control-slide-description"
                  rows={4}
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  disabled={submitting}
                  placeholder="What is this control set used for?"
                  className="mt-2 block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-600 focus:ring-indigo-600 disabled:bg-gray-100"
                />
              </div>
            </div>
          </section>

          <section aria-labelledby="slide-images-heading">
            <div className="flex items-end justify-between gap-4">
              <div>
                <h2 id="slide-images-heading" className="text-base font-semibold text-gray-900">Control images</h2>
                <p className="mt-1 text-sm text-gray-600">Upload exactly ten images. They are stored without background subtraction.</p>
                <p className="mt-2 text-sm text-gray-600">Use 2:3 portrait images to keep the entire picture. Other shapes are center-cropped to fit. After uploading, use Preview slide to check the result.</p>
              </div>
              <p className={`shrink-0 text-sm font-semibold ${items.length === CONTROL_SLIDE_IMAGE_COUNT ? 'text-emerald-700' : 'text-gray-700'}`}>
                {items.length} / {CONTROL_SLIDE_IMAGE_COUNT}
              </p>
            </div>

            <div
              onDragEnter={(event) => {
                event.preventDefault();
                setDragActive(true);
              }}
              onDragOver={(event) => event.preventDefault()}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
              className={`mt-6 rounded-lg border-2 border-dashed px-6 py-8 text-center transition-colors ${dragActive ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 bg-gray-50'}`}
            >
              <ArrowUpTrayIcon className="mx-auto h-8 w-8 text-gray-400" aria-hidden="true" />
              <div className="mt-3 flex justify-center text-sm text-gray-600">
                <label htmlFor="control-slide-images" className="cursor-pointer font-semibold text-indigo-600 hover:text-indigo-500">
                  Choose images
                  <input
                    id="control-slide-images"
                    type="file"
                    accept="image/*"
                    multiple
                    disabled={submitting}
                    onChange={(event) => {
                      addFiles(event.target.files);
                      event.target.value = '';
                    }}
                    className="sr-only"
                  />
                </label>
                <span className="pl-1">or drag and drop</span>
              </div>
              <p className="mt-1 text-xs text-gray-500">JPG, PNG, or other browser-supported image formats</p>
            </div>

            {fileError && <p role="alert" className="mt-3 text-sm font-medium text-red-700">{fileError}</p>}

            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
              {Array.from({ length: CONTROL_SLIDE_IMAGE_COUNT }, (_unused, index) => {
                const item = items[index];
                return (
                  <div
                    key={item?.id || `empty-${index}`}
                    data-testid={item ? 'control-slide-preview' : undefined}
                    className="group relative aspect-[2/3] overflow-hidden rounded-lg bg-gray-100 ring-1 ring-inset ring-gray-200"
                  >
                    {item ? (
                      <>
                        <img
                          src={item.previewUrl}
                          alt={`Preview ${index + 1}: ${item.file.name}`}
                          className="h-full w-full object-cover"
                        />
                        <span className="absolute bottom-2 left-2 rounded bg-black/70 px-2 py-1 text-xs font-semibold text-white">
                          {index + 1}
                        </span>
                        <button
                          type="button"
                          aria-label={`Remove ${item.file.name}`}
                          onClick={() => removeItem(item.id)}
                          disabled={submitting}
                          className="absolute right-2 top-2 rounded-full bg-white/95 p-1.5 text-gray-700 shadow-sm hover:bg-white hover:text-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                          <span className="sr-only">Remove {item.file.name}</span>
                        </button>
                      </>
                    ) : (
                      <div className="flex h-full flex-col items-center justify-center gap-2 text-gray-400">
                        <PhotoIcon className="h-6 w-6" aria-hidden="true" />
                        <span className="text-xs font-semibold">Image {index + 1}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        </div>

        {(validationErrors.length > 0 || submitError) && (
          <div role="alert" className="mt-8 rounded-lg border border-red-200 bg-red-50 p-4">
            <div className="flex gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-red-600" aria-hidden="true" />
              <div className="text-sm text-red-800">
                {submitError && <p className="font-semibold">{submitError}</p>}
                {validationErrors.map((error) => <p key={error}>{error}</p>)}
              </div>
            </div>
          </div>
        )}

        <footer className="mt-8 flex items-center justify-end gap-4 border-t border-gray-200 pt-6">
          {submitting && (
            <p role="status" className="text-sm font-medium text-gray-700">Uploading {progress}%</p>
          )}
          <button
            type="button"
            onClick={() => navigate('/control-slides')}
            disabled={submitting}
            className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={submitting || Boolean(organizationError)}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? 'Creating…' : 'Create control slide'}
          </button>
        </footer>
      </form>
    </div>
  );
}
